import type { PlayerLocationDeltas } from '@repo/types/maps/map-types';
import { gameCoordsToLatLng, remapGameCoords } from '@repo/types/maps/map-utils';
import type { MapHeatmapResponse } from '@repo/wemod-api-client/heatmap-api-client';
import type { HeatmapFeature, HeatmapFeatureCollection } from './heatmap-types';

/** Drop tail circles below this share so tiny clusters don't clutter the map. */
const MIN_CLUSTER_SHARE = 0.01;
const CIRCLE_POLYGON_STEPS = 48;

/**
 * Heatmap API `x`/`y` are game-world coordinates (same space as pin `gameCoords`).
 * Convert with the map's player-location deltas — do not use `world_to_deg` for placement
 * (that linear scale compresses everything near the origin on Night City).
 */
function gameWorldToLngLat(x: number, y: number, deltas: PlayerLocationDeltas): [number, number] | null {
    try {
        const remapped = remapGameCoords({ x, y, z: 0 }, deltas.deltas.gameCoordsMap);
        const lngLat = gameCoordsToLatLng(remapped, deltas.deltas);
        if (!lngLat) return null;
        return [lngLat.lng, lngLat.lat];
    } catch {
        return null;
    }
}

/** World-unit disk → closed lng/lat ring so coverage scales with zoom. */
function worldCirclePolygon(
    centerX: number,
    centerY: number,
    radiusWorld: number,
    deltas: PlayerLocationDeltas,
): [number, number][] | null {
    if (!(radiusWorld > 0)) return null;

    const ring: [number, number][] = [];
    for (let i = 0; i < CIRCLE_POLYGON_STEPS; i++) {
        const angle = (Math.PI * 2 * i) / CIRCLE_POLYGON_STEPS;
        const point = gameWorldToLngLat(
            centerX + Math.cos(angle) * radiusWorld,
            centerY + Math.sin(angle) * radiusWorld,
            deltas,
        );
        if (!point) return null;
        ring.push(point);
    }
    const first = ring[0];
    if (!first) return null;
    ring.push([first[0], first[1]]);
    return ring;
}

/**
 * Convert analytics heatmap payload into MapLibre GeoJSON.
 * - `bin` points → density heat (all zooms)
 * - `cluster` points → hotspot markers (only when `videos.sample` is non-empty)
 * - `cluster-area` polygons → world-unit coverage disks (scale with zoom)
 */
export function transformMapHeatmapResponse(
    response: MapHeatmapResponse,
    deltas: PlayerLocationDeltas,
): HeatmapFeatureCollection {
    const { bin_size: binSize, event_categories: categories } = response;
    const features: HeatmapFeature[] = [];
    const totals: Record<string, number> = {};

    for (const [categoryKey, category] of Object.entries(categories ?? {})) {
        totals[categoryKey] = category.total_events;
        const peak = Math.max(category.peak_cell, 1);

        for (const [binX, binY, count] of category.bins ?? []) {
            const worldX = binX * binSize;
            const worldY = binY * binSize;
            const centerDeg = gameWorldToLngLat(worldX, worldY, deltas);
            if (!centerDeg) continue;

            features.push({
                type: 'Feature',
                id: `${categoryKey}:bin:${binX}:${binY}`,
                geometry: {
                    type: 'Point',
                    coordinates: centerDeg,
                },
                properties: {
                    kind: 'bin',
                    event_type: categoryKey,
                    rank: 0,
                    events: count,
                    share: count / peak,
                    radius_world: binSize,
                },
            });
        }

        (category.clusters ?? []).forEach((cluster, index) => {
            if (cluster.share < MIN_CLUSTER_SHARE) return;

            const videoIds = (cluster.videos?.sample ?? []).filter(id => id.length > 0);
            // Markers exist to open clips — skip clusters with nothing to play.
            if (videoIds.length === 0) return;

            const centerDeg = gameWorldToLngLat(cluster.x, cluster.y, deltas);
            if (!centerDeg) return;

            const baseProps = {
                event_type: categoryKey,
                rank: index + 1,
                events: cluster.events,
                share: cluster.share,
                radius_world: cluster.r,
                video_ids: videoIds,
            } as const;

            features.push({
                type: 'Feature',
                id: `${categoryKey}:cluster:${index + 1}`,
                geometry: {
                    type: 'Point',
                    coordinates: centerDeg,
                },
                properties: {
                    kind: 'cluster',
                    ...baseProps,
                },
            });

            const ring = worldCirclePolygon(cluster.x, cluster.y, cluster.r, deltas);
            if (!ring) return;

            features.push({
                type: 'Feature',
                id: `${categoryKey}:cluster-area:${index + 1}`,
                geometry: {
                    type: 'Polygon',
                    coordinates: [ring],
                },
                properties: {
                    kind: 'cluster-area',
                    ...baseProps,
                },
            });
        });
    }

    return {
        type: 'FeatureCollection',
        meta: {
            event_types: Object.keys(categories ?? {}),
            totals,
        },
        features,
    };
}
